import type { Editor } from "@tiptap/core";
import type { EditorEvents } from "@tiptap/vue-3";
import type { Ref } from "vue";
import { whenever } from "@vueuse/core";
import { onBeforeUnmount, ref } from "vue";

export function getEditorElement(editor: Editor) {
  if (!editor) return undefined;

  const element = editor.options.element;
  if (!element) return undefined;
  if (element instanceof Element) {
    return element;
  }

  if (typeof element === "object" && "mount" in element) {
    return element.mount;
  }

  if (typeof element === "function") {
    return undefined;
  }

  return element ?? undefined;
}

export function useEditorRootElement(editor: Ref<Editor | undefined>) {
  const editorElement = ref<HTMLElement>();
  const counterKey = ref(0);

  // because editor.options.element isn't reactive attach event handler to 'update' event and detect when element has changed
  // watch for changes to rootElement of Editor to reconstruct components that depend on presence of parent HTMLElement
  // use counter as a simple key to invalidate and recycle dependent components

  function handleUpdate(ctx: EditorEvents["update"]) {
    const newEditorElement = getEditorElement(ctx.editor);
    if (editorElement.value !== newEditorElement) {
      editorElement.value = newEditorElement as HTMLElement;
      counterKey.value += 1;
    }
  }

  whenever(editor, editorReady => {
    editorReady.on("update", handleUpdate);
  }, { immediate: true });

  onBeforeUnmount(() => {
    if (editor.value) {
      editor.value.off("update", handleUpdate);
    }
  });

  return {
    key: counterKey,
    editorElement,
  };
}
